import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

import { Formik, FormikValues } from "formik";
import { Dispatch, bindActionCreators } from "redux";

import { Overlay, ActionBar, Form, ApplyButton, OkButton, CancelLink, ColumnType } from "@plex/react-components";

import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { useLocalizedValues, useLocalizer, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import MessageFormatSection from "./message-format-section/MessageFormatSection";
import { messageFormatActions } from "./MessageId.actions";
import { validateMessageId, validateTextField, validateTokenDescriptions } from "./MessageId.helpers";
import styles from "./MessageId.module.scss";
import { IMessageIdState, IValidationResult, IToken, ITokenDescriptionValidationResult } from "./MessageId.state";
import TextPreviewSection from "./text-preview-section/TextPreviewSection";
import TokensSection from "./tokens-section/TokensSection";

interface IMessageIdProps {
  messageIdParameter: string;
  messageId: string;
  text: string;
  status: string;
  tokens: IToken[];
  isLoading: boolean;
  shouldRedirect: boolean;
  redirectPath: string;
  notifications: INotification[];
  addMessage: (redirectPath: string) => void;
  updateMessage: (redirectPath: string) => void;
  loadMessageData: (messageId: string) => void;
  approveMessage: (messageId: string) => void;
  obsoleteMessage: (messageId: string) => void;
  deprecateMessage: (messageId: string) => void;
  cancelLinkClick: () => void;
  setValidationBanner: (flag: boolean) => void;
  setMessageIdValidationError: (error: string) => void;
  setTextValidationError: (error: string) => void;
  setTokensDescriptionValidationState: (validationState: ITokenDescriptionValidationResult) => void;
}

const MessageId: React.FC<IMessageIdProps> = ({
  messageIdParameter,
  messageId,
  text,
  status,
  tokens,
  isLoading,
  shouldRedirect,
  redirectPath,
  notifications,
  addMessage,
  updateMessage,
  loadMessageData,
  approveMessage,
  obsoleteMessage,
  deprecateMessage,
  cancelLinkClick,
  setValidationBanner,
  setMessageIdValidationError,
  setTextValidationError,
  setTokensDescriptionValidationState
}) => {
  const localizedValues = useLocalizedValues(() => ({
    historyButtonText: displayMessages.action("messageIdHistory"),
    approveButtonText: displayMessages.action("messageIdApprove"),
    deprecateButtonText: displayMessages.action("messageIdDeprecate"),
    obsoleteButtonText: displayMessages.action("messageIdObsolete"),
    releaseButtonText: displayMessages.action("messageIdRelease"),
    stageButtonText: displayMessages.action("messageIdStage"),
    unstageButtonText: displayMessages.action("messageIdUnstage")
  }));

  const history = useHistory();
  const localizer = useLocalizer();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const [applyFlag, setApplyFlag] = useState<boolean>(false);
  const [formColumnLayout, setFormColumnLayout] = useState<ColumnType>(ColumnType.double);

  const doubleColumnLayoutMinWidth = 1400;

  useEffect(() => {
    messageIdParameter && loadMessageData(messageIdParameter);

    const resizeWindowHandler = e => setFormColumnLayout(e.target?.innerWidth < doubleColumnLayoutMinWidth ? ColumnType.single : ColumnType.double);
    window.addEventListener("resize", resizeWindowHandler);

    return () => window.removeEventListener("resize", resizeWindowHandler);
  }, [messageIdParameter, loadMessageData]);

  useEffect(() => {
    if (shouldRedirect) {
      history.push(redirectPath);
    }
  }, [shouldRedirect, history, redirectPath]);

  const onApproveClick = useCallback(() => {
    approveMessage(messageIdParameter);
  }, [approveMessage, messageIdParameter]);

  const onObsoleteClick = useCallback(() => {
    obsoleteMessage(messageIdParameter);
  }, [obsoleteMessage, messageIdParameter]);

  const onDeprecateClick = useCallback(() => {
    deprecateMessage(messageIdParameter);
  }, [deprecateMessage, messageIdParameter]);

  const onSubmitClick = useCallback((handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void, applying: boolean) => {
    handleSubmit();
    setApplyFlag(applying);
  }, []);

  const disableApproveButton = useCallback(() => {
    return status !== displayMessages.option("messageStatusNew").value;
  }, [status]);

  const disableObsoleteButton = useCallback(() => {
    return status === displayMessages.option("messageStatusObsolete").value;
  }, [status]);

  const disableDeprecateButton = useCallback(() => {
    return status === displayMessages.option("messageStatusDeprecated").value || status === displayMessages.option("messageStatusObsolete").value;
  }, [status]);

  const localizeValidationResult = (result?: IValidationResult): string => {
    if (!result || !result.errorMessageId) {
      return "";
    }

    const localizedTokens = {};
    for (const name in result.tokens) {
      if (result.tokens.hasOwnProperty(name)) {
        localizedTokens[name] = localizer(result.tokens[name]);
      }
    }

    return localizer(result.errorMessageId, localizedTokens);
  };

  return (
    <>
      <Overlay show={isLoading} showSpinner />
      {messageIdParameter && roles.PlexTechnicalWriter ? (
        <ActionBar>
          <ActionBar.Button
            id="approveButton"
            text={localizedValues.approveButtonText}
            title={localizedValues.approveButtonText}
            disabled={disableApproveButton()}
            onClick={onApproveClick}
          />
          <ActionBar.Button
            id="deprecateButton"
            text={localizedValues.deprecateButtonText}
            title={localizedValues.deprecateButtonText}
            disabled={disableDeprecateButton()}
            onClick={onDeprecateClick}
          />
          <ActionBar.Button
            id="obsoleteButton"
            text={localizedValues.obsoleteButtonText}
            title={localizedValues.obsoleteButtonText}
            disabled={disableObsoleteButton()}
            onClick={onObsoleteClick}
          />
          <ActionBar.Button id="historyButton" text={localizedValues.historyButtonText} title={localizedValues.historyButtonText} disabled={true} />
        </ActionBar>
      ) : (
        <ActionBar />
      )}
      <Formik
        initialValues={{
          messageId: ""
        }}
        validate={(_values: FormikValues) => {
          let errors: any = {};

          let result: IValidationResult = validateMessageId(messageId);
          if (result.success === false) {
            errors.messageIdInput = localizeValidationResult(result);
            setMessageIdValidationError(errors.messageIdInput);
          } else {
            setMessageIdValidationError("");
          }

          result = validateTextField(text);
          if (result.success === false) {
            errors.messageText = localizeValidationResult(result);
            setTextValidationError(errors.messageText);
          } else {
            setTextValidationError("");
          }

          const descriptionValidationResult = validateTokenDescriptions(tokens);
          setTokensDescriptionValidationState(descriptionValidationResult[1]);

          const descriptionValidationErrors = {};
          Object.values(descriptionValidationResult[1])
            .filter(v => v.isValid === false)
            .forEach(v => (descriptionValidationErrors[v.tokenId] = true));

          if (descriptionValidationResult[0] === false) {
            errors = { ...errors, ...descriptionValidationErrors };
          }

          setValidationBanner(Object.getOwnPropertyNames(errors).length > 0);
          return errors;
        }}
        onSubmit={(_: FormikValues, { setSubmitting }) => {
          setSubmitting(false);
          if (applyFlag) {
            messageIdParameter ? updateMessage("") : addMessage("/message-details/" + messageId);
          } else {
            messageIdParameter ? updateMessage("/messages") : addMessage("/messages");
          }
        }}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={() => onSubmitClick(handleSubmit, false)} className={styles.messageFormTab}>
            <Form.Header>
              <Banner messages={notifications} />
            </Form.Header>
            <Form.Body layout={formColumnLayout} className={styles.messageFormBody}>
              <MessageFormatSection messageIdParameter={messageIdParameter} localizeValidationResult={localizeValidationResult} />
              <TokensSection />
            </Form.Body>
            <Form.Body>
              <TextPreviewSection />
            </Form.Body>
            <Form.Footer className={styles.formFooter}>
              <CancelLink onClick={cancelLinkClick} />
              {roles.PlexTechnicalWriter && (
                <>
                  <ApplyButton onClick={() => onSubmitClick(handleSubmit, true)} />
                  <OkButton onClick={() => onSubmitClick(handleSubmit, false)} />
                </>
              )}
            </Form.Footer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default connect(
  (state: { messageFormat: IMessageIdState }) => {
    return {
      messageId: state.messageFormat.messageId,
      text: state.messageFormat.text,
      status: state.messageFormat.currentStatus,
      tokens: state.messageFormat.tokens,
      isLoading: state.messageFormat.isLoading,
      shouldRedirect: state.messageFormat.shouldRedirect,
      redirectPath: state.messageFormat.redirectPath,
      notifications: state.messageFormat.notifications
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        addMessage: messageFormatActions.addMessageId,
        updateMessage: messageFormatActions.updateMessageId,
        loadMessageData: messageFormatActions.loadMessageData,
        approveMessage: messageFormatActions.approveMessage,
        obsoleteMessage: messageFormatActions.obsoleteMessage,
        deprecateMessage: messageFormatActions.deprecateMessage,
        cancelLinkClick: messageFormatActions.cancelLinkClick,
        setValidationBanner: messageFormatActions.setValidationBanner,
        setMessageIdValidationError: messageFormatActions.setMessageIdValidationError,
        setTextValidationError: messageFormatActions.setTextValidationError,
        setTokensDescriptionValidationState: messageFormatActions.setTokensDescriptionValidationState
      },
      dispatch
    )
)(MessageId);
